import { PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'

import history from '@/history'
import i18n from '@/i18n'
import { messageHandlerSet } from '@/redux/messageHandler/actions'
import * as ScheduleTemplatesAPI from '@/services/scheduleTemplates'
import * as StNotesAPI from '@/services/stNotes'
import * as StScheduledExercisesAPI from '@/services/stScheduledExercise'
import * as StScheduledExerciseSettingsAPI from '@/services/stScheduledExerciseSettings'
import { StScheduledExercise } from '@/types'
import {
  addScheduleTemplateFailed,
  addScheduleTemplateRequest,
  addScheduleTemplateSuccess,
  addStScheduledExerciseFailed,
  addStScheduledExerciseRequest,
  addStScheduledExerciseSuccess,
  deleteStScheduledExercisesByIdsFailed,
  deleteStScheduledExercisesByIdsRequest,
  deleteStScheduledExercisesByIdsSuccess,
  editScheduleTemplateFailed,
  editScheduleTemplateRequest,
  editScheduleTemplateSuccess,
  editStScheduledExercisesByIdsFailed,
  editStScheduledExercisesByIdsRequest,
  editStScheduledExercisesByIdsSuccess,
  editStScheduledExerciseSettingsByIdsFailed,
  editStScheduledExerciseSettingsByIdsRequest,
  editStScheduledExerciseSettingsByIdsSuccess,
  getScheduleTemplatesFailed,
  getScheduleTemplatesRequest,
  getScheduleTemplatesSuccess,
  getSingleScheduleTemplateFailed,
  getSingleScheduleTemplateRequest,
  getSingleScheduleTemplateSuccess,
  removeScheduleTemplateFailed,
  removeScheduleTemplateRequest,
  removeScheduleTemplateSuccess
} from './actions'
import type {
  AddScheduleTemplateRequestPayload,
  AddStScheduledExerciseRequestPayload,
  DeleteStScheduledExercisesByIdsRequestPayload,
  EditScheduleTemplateRequestPayload,
  EditStScheduledExercisesByIdsRequestPayload,
  EditStScheduledExerciseSettingsByIdsRequestPayload,
  GetSingleScheduleTemplateRequestPayload,
  ISingleScheduleTemplate,
  RemoveScheduleTemplateRequestPayload,
  ScheduleTemplateModel
} from './types'

function * getScheduleTemplatesSaga () {
  try {
    const scheduleTemplatesData: ScheduleTemplateModel[] = yield call(
      ScheduleTemplatesAPI.getScheduleTemplates
    )

    if (scheduleTemplatesData) {
      yield put(getScheduleTemplatesSuccess({ scheduleTemplatesData }))
    } else {
      yield put(getScheduleTemplatesFailed())
    }
  } catch (err) {
    yield put(getScheduleTemplatesFailed())
  }
}

function * getSingleScheduleTemplateSaga ({
  payload
}: PayloadAction<GetSingleScheduleTemplateRequestPayload>) {
  const { id } = payload

  try {
    const singleScheduleTemplateData: ISingleScheduleTemplate = yield call(
      ScheduleTemplatesAPI.getSingleScheduleTemplate,
      { id }
    )

    if (!singleScheduleTemplateData) {
      throw Error
    }

    yield put(
      getSingleScheduleTemplateSuccess({
        singleScheduleTemplateData
      })
    )
  } catch (err) {
    yield put(getSingleScheduleTemplateFailed())
  }
}

function * deleteScheduleTemplateSaga ({
  payload
}: PayloadAction<RemoveScheduleTemplateRequestPayload>) {
  const { id } = payload

  try {
    yield call(ScheduleTemplatesAPI.deleteScheduleTemplate, {
      id
    })

    yield put(removeScheduleTemplateSuccess({ id }))

    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.schedule_template_removed'),
        type: 'success'
      })
    )
  } catch (err) {
    yield put(removeScheduleTemplateFailed())
  }
}

function * editScheduleTemplateSaga ({
  payload
}: PayloadAction<EditScheduleTemplateRequestPayload>) {
  const { id, ...fieldsToEdit } = payload

  try {
    const res: AxiosResponse<ScheduleTemplateModel> = yield call(
      ScheduleTemplatesAPI.editScheduleTemplate,
      {
        id,
        fieldsToEdit
      }
    )

    if (!res.data) {
      throw Error
    }

    yield put(editScheduleTemplateSuccess(res.data))

    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.schedule_template_updated'),
        type: 'success'
      })
    )
  } catch (err) {
    yield put(editScheduleTemplateFailed())
  }
}

function * addScheduleTemplateSaga ({
  payload
}: PayloadAction<AddScheduleTemplateRequestPayload>) {
  const { redirect, stNotes, ...scheduleTemplateData } = payload
  try {
    const res: AxiosResponse = yield call(
      ScheduleTemplatesAPI.addScheduleTemplate,
      scheduleTemplateData
    )

    if (res.status !== 200) {
      throw Error
    }

    if (stNotes && stNotes.length > 0) {
      yield call(StNotesAPI.addBatchStNote, {
        scheduleId: res.data.id,
        notes: stNotes.map(({ text }, index) => ({
          text,
          order: index
        }))
      })
    }

    yield put(addScheduleTemplateSuccess())

    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.schedule_template_added'),
        type: 'success'
      })
    )

    if (redirect === 'edit-schedule-template') {
      history.replace(`/edit-schedule-template/${res.data.id}`)

      return
    }
    if (redirect) history.replace(redirect)
  } catch (err) {
    console.log(err)
    yield put(addScheduleTemplateFailed())
  }
}

function * deleteStScheduledExercisesByIdsSaga ({
  payload
}: PayloadAction<DeleteStScheduledExercisesByIdsRequestPayload>) {
  const { stScheduledExerciseIds, scheduleId } = payload
  try {
    yield call(StScheduledExercisesAPI.deleteBatchStScheduledExercise, {
      stScheduledExerciseId: stScheduledExerciseIds
    })

    yield put(
      deleteStScheduledExercisesByIdsSuccess({
        stScheduledExerciseIds,
        scheduleId
      })
    )
  } catch (err) {
    console.error(err)
    yield put(deleteStScheduledExercisesByIdsFailed())
  }
}

function * editStScheduledExercisesByIdsSaga ({
  payload
}: PayloadAction<EditStScheduledExercisesByIdsRequestPayload>) {
  const { stScheduledExercisesData } = payload
  try {
    yield call(StScheduledExercisesAPI.editBatchStScheduledExercise, {
      stScheduledExercisesData
    })

    yield put(editStScheduledExercisesByIdsSuccess(
      payload
    ))
  } catch (err) {
    console.error(err)
    yield put(editStScheduledExercisesByIdsFailed())
  }
}

function * addStScheduledExerciseSaga ({
  payload
}: PayloadAction<AddStScheduledExerciseRequestPayload>) {
  try {
    const responseData: StScheduledExercise[] = yield call(
      StScheduledExercisesAPI.addBatchStScheduledExercise,
      {
        addStScheduledExerciseRequestPayload: {
          exercises: payload.exercises,
          tplScheduleId: payload.tplScheduleId
        }
      }
    )

    yield put(
      addStScheduledExerciseSuccess({
        stScheduledExercise: responseData,
        scheduleId: payload.tplScheduleId
      })
    )
  } catch (err) {
    console.error(err)
    yield put(addStScheduledExerciseFailed())
  }
}

function * editStScheduledExerciseSettingsByIdsSaga ({
  payload
}: PayloadAction<EditStScheduledExerciseSettingsByIdsRequestPayload>) {
  try {
    const { exercisesIds, value, textId } = payload

    yield call(StScheduledExerciseSettingsAPI.editBatchStScheduledExerciseSetting, {
      editStScheduledExerciseSettingRequestPayload: {
        settings: exercisesIds.map(exerciseId => ({
          scheduledExerciseId: exerciseId,
          settingTextId: textId,
          newValue: value
        }))
      }
    })

    yield put(editStScheduledExerciseSettingsByIdsSuccess(payload))
  } catch (err) {
    console.error(err)
    yield put(editStScheduledExerciseSettingsByIdsFailed())
  }
}

function * scheduleTemplatesSaga () {
  yield takeLatest(getScheduleTemplatesRequest.type, getScheduleTemplatesSaga)
  yield takeLatest(
    removeScheduleTemplateRequest.type,
    deleteScheduleTemplateSaga
  )
  yield takeLatest(addScheduleTemplateRequest.type, addScheduleTemplateSaga)
  yield takeLatest(editScheduleTemplateRequest.type, editScheduleTemplateSaga)
  yield takeLatest(
    getSingleScheduleTemplateRequest.type,
    getSingleScheduleTemplateSaga
  )
  yield takeEvery(
    deleteStScheduledExercisesByIdsRequest.type,
    deleteStScheduledExercisesByIdsSaga
  )
  yield takeEvery(
    editStScheduledExercisesByIdsRequest.type,
    editStScheduledExercisesByIdsSaga
  )
  yield takeEvery(
    addStScheduledExerciseRequest.type,
    addStScheduledExerciseSaga
  )
  yield takeLatest(
    editStScheduledExerciseSettingsByIdsRequest.type,
    editStScheduledExerciseSettingsByIdsSaga
  )
}

export default scheduleTemplatesSaga
